import { RequestedUpdatesGrouped } from "../../../data/dtos/onboarding-status.dto";
import { getFormikFieldName } from "../../Profile/information/entity/entityFieldKeyMap";
import { isItemResolved } from "../../../utils/updatesRequiredResolution";
import { useGetDocumentsQuery } from "../../../data/useProfile";
import useUserInfo from "../../../hooks/useUserInfo";

/**
 * Counts of still-unresolved flagged items per onboarding section, used for
 * both submit-gating (EntityOnboardingHeader) and the per-section subtitle
 * copy (EntityOnboardingCard) so both stay in sync with the same definition
 * of "resolved".
 */
export function useUnresolvedSectionCounts(
  requestedUpdatesGrouped?: RequestedUpdatesGrouped,
) {
  const { investorId } = useUserInfo();
  const hasFlaggedDocuments =
    !!requestedUpdatesGrouped?.documentComments?.length;
  const documentsQuery = useGetDocumentsQuery({ enabled: hasFlaggedDocuments });

  // Uploaded document IDs currently present, used to tell whether a flagged
  // document has already been replaced (its old documentId simply won't be
  // in this set any more).
  const uploadedDocumentIds = new Set(
    documentsQuery.data?.uploadedDocuments.map((doc) => doc.documentId) ?? [],
  );

  const roundId = requestedUpdatesGrouped?.requestedAt;

  const unresolvedEntityInformationCount = (
    requestedUpdatesGrouped?.entityInformationComments ?? []
  ).filter(
    ({ fieldKey }) =>
      !isItemResolved(investorId, getFormikFieldName(fieldKey), roundId),
  ).length;

  const documentComments = requestedUpdatesGrouped?.documentComments ?? [];

  // Fail closed while the uploaded-documents list hasn't loaded yet: without
  // it we can't tell a replaced document from a still-flagged one, and
  // treating them all as resolved would briefly zero out the count used for
  // both the section badge and submit-gating.
  const unresolvedDocumentsCount = !documentsQuery.data
    ? documentComments.length
    : documentComments.filter(({ documentId }) =>
        uploadedDocumentIds.has(documentId),
      ).length;

  const unresolvedKeyPartiesCount = (
    requestedUpdatesGrouped?.keyPartyComments ?? []
  ).filter(
    ({ legalSignerId }) => !isItemResolved(investorId, legalSignerId, roundId),
  ).length;

  return {
    unresolvedEntityInformationCount,
    unresolvedDocumentsCount,
    unresolvedKeyPartiesCount,
  };
}
